// Platform walkthrough steps — premium card grid for sales pages
"use client";

import React from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Wifi,
  PackageCheck,
  ShieldCheck,
  BarChart3,
  type LucideIcon,
} from "lucide-react";

interface PlatformStep {
  step: number;
  title: string;
  description: string;
}

interface PlatformStepsProps {
  steps: PlatformStep[];
  className?: string;
}

interface StepStyle {
  icon: LucideIcon;
  gradient: string;
  bg: string;
  text: string;
}

const stepStyles: StepStyle[] = [
  { icon: Building2, gradient: "from-emerald-500 to-teal-600", bg: "bg-emerald-50", text: "text-emerald-600" },
  { icon: Wifi, gradient: "from-blue-500 to-indigo-600", bg: "bg-blue-50", text: "text-blue-600" },
  { icon: PackageCheck, gradient: "from-purple-500 to-violet-600", bg: "bg-purple-50", text: "text-purple-600" },
  { icon: ShieldCheck, gradient: "from-amber-500 to-orange-600", bg: "bg-amber-50", text: "text-amber-600" },
  { icon: BarChart3, gradient: "from-rose-500 to-pink-600", bg: "bg-rose-50", text: "text-rose-600" },
];

export function PlatformSteps({ steps, className = "" }: PlatformStepsProps) {
  return (
    <div className={`w-full ${className}`}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="text-left mb-8 sm:mb-10 lg:mb-14 px-3 sm:px-0"
      >
        <span className="inline-block px-4 py-1.5 rounded-full bg-purple-50 border border-purple-100 text-purple-600 text-xs font-semibold uppercase tracking-wider mb-4">
          Platform walkthrough
        </span>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-satoshi font-black text-gray-900 mb-3">
          How it works
        </h2>
        <p className="text-sm sm:text-base lg:text-lg font-light text-gray-500 max-w-2xl leading-relaxed">
          From onboarding to full portfolio visibility in five steps
        </p>
      </motion.div>

      {/* Step cards — flex-wrap so the last row auto-centres */}
      <div className="px-3 sm:px-0">
        <div className="flex flex-wrap justify-start gap-4 lg:gap-5">
          {steps.map((item, i) => {
            const style = stepStyles[i] || stepStyles[0];
            const Icon = style.icon;

            return (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.833rem)]"
              >
                <div className="relative overflow-hidden rounded-2xl bg-white border border-gray-100 p-5 lg:p-6 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 h-full group">
                  {/* Gradient top accent */}
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${style.gradient}`} />

                  {/* Large watermark number */}
                  <span className="absolute -right-2 -top-3 text-[84px] font-black text-violet-300/80 select-none pointer-events-none leading-none group-hover:text-violet-400/80 transition-colors duration-300">
                    {item.step}
                  </span>

                  {/* Content */}
                  <div className="relative z-10">
                    <div className={`w-10 h-10 rounded-xl ${style.bg} flex items-center justify-center mb-4`}>
                      <Icon className={`w-5 h-5 ${style.text}`} />
                    </div>

                    <span className={`text-[11px] font-bold ${style.text} uppercase tracking-wider`}>
                      Step {item.step}
                    </span>

                    <h3 className="text-base lg:text-lg font-bold text-gray-900 mt-1.5 mb-2 leading-snug">
                      {item.title}
                    </h3>

                    <p className="text-sm text-gray-500 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
